import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import { 
  Package,
  Upload,
  FileCheck,
  Calculator,
  ArrowRight,
  ArrowLeft,
  Check,
  Search,
  Calendar,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  Loader2,
  Info,
  Plus,
  X
} from "lucide-react";
import NestingDiagram from "@/components/NestingDiagram";

const PER_PAGE = 50;

export default function CreateOrderPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);

  // Step 1: Product Selection
  const [groups, setGroups] = useState([]);
  const [products, setProducts] = useState([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [productSearch, setProductSearch] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [step1SortField, setStep1SortField] = useState(null);
  const [step1SortDir, setStep1SortDir] = useState("asc");

  const handleStep1Sort = (field) => {
    if (step1SortField === field) setStep1SortDir(d => d === "asc" ? "desc" : "asc");
    else { setStep1SortField(field); setStep1SortDir("asc"); }
  };

  const Step1SortIcon = ({ field }) => {
    if (step1SortField !== field) return <ChevronsUpDown size={11} className="ml-1 opacity-40 inline-block" />;
    return step1SortDir === "asc" ? <ChevronUp size={11} className="ml-1 inline-block" /> : <ChevronDown size={11} className="ml-1 inline-block" />;
  };

  const sortedProducts = useMemo(() => {
    if (!step1SortField) return products;
    return [...products].sort((a, b) => {
      let av = a[step1SortField] ?? 0, bv = b[step1SortField] ?? 0;
      if (typeof av === "string") return step1SortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      return step1SortDir === "asc" ? av - bv : bv - av;
    });
  }, [products, step1SortField, step1SortDir]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const searchTimerRef = useRef(null);

  // Step 2: Design Upload
  const [orderItems, setOrderItems] = useState([]);
  const [uploading, setUploading] = useState({});

  const [costPreview, setCostPreview] = useState(null);
  const [loadingCost, setLoadingCost] = useState(false);

  // Step 3: Summary
  const [customerNotes, setCustomerNotes] = useState("");
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState("");
  const [deliverySchedule, setDeliverySchedule] = useState([]);
  const [focusScheduleIdx, setFocusScheduleIdx] = useState(null);
  const scheduleQtyRefs = useRef({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (focusScheduleIdx !== null && scheduleQtyRefs.current[focusScheduleIdx]) {
      scheduleQtyRefs.current[focusScheduleIdx].focus();
      scheduleQtyRefs.current[focusScheduleIdx].select();
      setFocusScheduleIdx(null);
    }
  }, [focusScheduleIdx, deliverySchedule.length]);

  const addScheduleRow = () => {
    setDeliverySchedule(prev => {
      setFocusScheduleIdx(prev.length);
      return [...prev, { date: "", quantity: orderItems[0]?.quantity || 1 }];
    });
  };
  const removeScheduleRow = (idx) => setDeliverySchedule(prev => prev.filter((_, i) => i !== idx));
  const updateScheduleRow = (idx, field, value) => setDeliverySchedule(prev => {
    const rows = [...prev];
    rows[idx] = { ...rows[idx], [field]: value };
    return rows;
  });

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const res = await api.get("/products/groups");
        setGroups(res.data.groups || []);
      } catch (error) {
        toast.error("Failed to load product groups");
      }
    };
    fetchGroups();
  }, []);

  const fetchProducts = useCallback(async (page, search, group) => {
    setSearchLoading(true);
    try {
      const params = new URLSearchParams({ page, per_page: PER_PAGE });
      if (search) params.append("search", search);
      if (group) params.append("group", group);
      const res = await api.get(`/products?${params.toString()}`);
      setProducts(res.data.items || []);
      setTotalProducts(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
    } catch (error) {
      toast.error("Failed to load products");
    } finally {
      setSearchLoading(false);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts(currentPage, productSearch, selectedGroup);
  }, [currentPage, selectedGroup, fetchProducts]);

  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setCurrentPage(1);
      fetchProducts(1, productSearch, selectedGroup);
    }, 400);
    return () => clearTimeout(searchTimerRef.current);
  }, [productSearch]);

  const selectProduct = (product) => {
    setSelectedProducts([product]);
    setOrderItems([{ product, design: null, quantity: 1, component_name: "", cost: null }]);
    setStep(2);
  };

  const SUPPORTED_EXTENSIONS = ['.pds', '.dxf', '.pdf', '.ai', '.cdr', '.plt'];

  const handleFileUpload = async (productId, file, index) => {
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!SUPPORTED_EXTENSIONS.includes(ext)) {
      toast.error(`Unsupported format "${ext}". Upload .PDS, .DXF, .PDF, .AI or .CDR files.`);
      return;
    }
    setUploading(prev => ({ ...prev, [productId]: true }));
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await api.post(`/designs/upload?product_id=${productId}`, formData);
      setOrderItems(prev => prev.map((item, i) => i === index ? { ...item, design: res.data } : item));
      toast.success(`Design uploaded for ${orderItems[index].product.product_name}`);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : detail
          ? JSON.stringify(detail)
          : (error.message || "Upload failed.");
      toast.error(msg);
    } finally {
      setUploading(prev => ({ ...prev, [productId]: false }));
    }
  };

  const updateQuantity = (index, quantity) => {
    setOrderItems(prev => prev.map((item, i) => i === index ? { ...item, quantity: Math.max(1, quantity) } : item));
  };

  const updateComponentName = (index, name) => {
    setOrderItems(prev => prev.map((item, i) => i === index ? { ...item, component_name: name } : item));
  };

  const handlePlaceOrder = async () => {
    setSubmitting(true);
    try {
      const items = orderItems.map(item => ({
        product_id: item.product.id,
        design_id: item.design.id,
        quantity: item.quantity,
        component_name: item.component_name || item.design?.filename || "Component"
      }));
      await api.post("/quotations", {
        items,
        customer_notes: customerNotes || null,
        expected_delivery_date: expectedDeliveryDate || null,
        delivery_schedule: deliverySchedule.filter(r => r.date && r.quantity > 0),
      });
      toast.success("Order placed successfully!");
      navigate("/dashboard/quotations");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to place order");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveDraft = async () => {
    setSubmitting(true);
    try {
      const items = orderItems.map(item => ({
        product_id: item.product.id,
        design_id: item.design.id,
        quantity: item.quantity,
        component_name: item.component_name || item.design?.filename || "Component"
      }));
      await api.post("/quotations/draft", {
        items,
        customer_notes: customerNotes || null,
        expected_delivery_date: expectedDeliveryDate || null,
        delivery_schedule: deliverySchedule.filter(r => r.date && r.quantity > 0),
      });
      toast.success("Draft saved!");
      navigate("/dashboard/quotations");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save draft");
    } finally {
      setSubmitting(false);
    }
  };

  const canProceedToStep2 = selectedProducts.length > 0;
  const canProceedToStep3 = orderItems.every(item => item.design !== null);

  const goToStep = async (newStep) => {
    if (newStep === 3) {
      setLoadingCost(true);
      try {
        const items = orderItems.map(item => ({
          product_id: item.product.id,
          design_id: item.design.id,
          quantity: item.quantity,
          component_name: item.component_name || item.design?.filename || "Component"
        }));
        const res = await api.post("/quotations/preview", { items });
        if (!res.data?.items?.length) {
          toast.error("No cost data returned.");
          setLoadingCost(false);
          return;
        }
        setCostPreview(res.data);
        if (res.data.estimated_delivery_date && !expectedDeliveryDate) {
          setExpectedDeliveryDate(res.data.estimated_delivery_date);
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to calculate costs.");
        setLoadingCost(false);
        return;
      } finally {
        setLoadingCost(false);
      }
    }
    setStep(newStep);
  };

  // Recalculate preview with updated order items (used after applying qty suggestion)
  const recalculateCost = async (updatedOrderItems) => {
    setLoadingCost(true);
    try {
      const items = updatedOrderItems.map(item => ({
        product_id: item.product.id,
        design_id: item.design.id,
        quantity: item.quantity,
        component_name: item.component_name || item.design?.filename || "Component"
      }));
      const res = await api.post("/quotations/preview", { items });
      setCostPreview(res.data);
    } catch (error) {
      toast.error("Failed to recalculate costs");
    } finally {
      setLoadingCost(false);
    }
  };

  // Returns an optimal quantity suggestion for an item, or null
  const suggestOptimalQty = (item) => {
    const sheets = item.nesting_sheets_used;
    if (!sheets || sheets <= 1) return null;
    const placements = item.placements || [];
    if (placements.length === 0) return null;

    // Count pieces per sheet from placement data
    const perSheet = {};
    placements.forEach(p => {
      const s = p.sheet ?? 0;
      perSheet[s] = (perSheet[s] || 0) + 1;
    });

    const counts = Object.values(perSheet);
    const maxPcs = Math.max(...counts);
    const minPcs = Math.min(...counts);
    const fullCapacity = maxPcs * sheets;

    // Suggest filling last sheet to match the fullest sheet
    if (fullCapacity > item.quantity) {
      const extra = fullCapacity - item.quantity;
      return {
        suggestedQty: fullCapacity,
        extra,
        direction: "up",
        message: `Add ${extra} more piece${extra > 1 ? "s" : ""} to fill all ${sheets} sheets completely`,
        detail: `Current: ${item.quantity} pcs across ${sheets} sheets → Suggested: ${fullCapacity} pcs (${maxPcs} per sheet)`
      };
    }

    // Suggest removing partially-filled last sheet if it's less than 40% full
    if (minPcs < maxPcs * 0.4 && sheets > 1) {
      const lastSheetPcs = counts[counts.length - 1];
      const reducedQty = item.quantity - lastSheetPcs;
      if (reducedQty > 0) {
        return {
          suggestedQty: reducedQty,
          extra: -lastSheetPcs,
          direction: "down",
          message: `Remove ${lastSheetPcs} piece${lastSheetPcs > 1 ? "s" : ""} to eliminate the last near-empty sheet`,
          detail: `Current: ${item.quantity} pcs (last sheet only ${lastSheetPcs}/${maxPcs}) → Saves 1 full sheet of material`
        };
      }
    }

    return null;
  };

  // Helper: compute price per sq meter
  const getPricePerSqMeter = (item) => {
    if (item.applied_rate_uom === "SQM") return item.applied_rate;
    if (item.applied_rate_uom === "MTR") return (item.applied_rate / ((item.product_width_mm || 1000) / 1000));
    return item.applied_rate;
  };

  // Helper: UOM-aware material consumption display
  // Net = sheets × length / 1000 mtr (for ROLL: sheets=1, length=nesting_length_used_mm)
  const getMaterialDisplay = (item) => {
    const uom = (item.applied_rate_uom || "").toUpperCase();
    const qty = item.quantity || 1;
    const sheets = item.nesting_sheets_used ?? 1;
    const sheetLen = item.product_length_mm || item.nesting_length_used_mm || 1000;
    const netMtr = (sheets * sheetLen) / 1000;
    const perQty = `${((item.shapes_area_sqmm || 0) / qty / 1_000_000).toFixed(4)} sq.m`;

    if (uom === "MTR") return { perQty, net: `${netMtr.toFixed(3)} mtr`, rate: `₹${item.applied_rate}/mtr` };
    if (uom === "SHEET") return { perQty, net: `${netMtr.toFixed(3)} mtr`, rate: `₹${item.applied_rate}/sheet` };
    if (uom === "PCS" || uom === "ROL") return { perQty, net: `${netMtr.toFixed(3)} mtr`, rate: `₹${item.applied_rate}/pc` };
    return { perQty, net: `${netMtr.toFixed(3)} mtr`, rate: `₹${item.applied_rate}/sq.m` };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-slate-900 border-t-transparent animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-120px)] pb-16" data-testid="create-order-page">
      {/* Compact Header with Search */}
      <div className="flex-shrink-0 pb-3">
        <div className="flex items-center justify-between border-b border-slate-200 pb-3">
          <div className="flex items-center gap-4">
            <h1 className="font-heading text-2xl font-bold text-slate-900 uppercase tracking-tight" data-testid="create-order-title">
              Create Order
            </h1>
            {step === 1 && (
              <div className="relative w-56">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
                <Input
                  placeholder="Search products..."
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  className="pl-8 rounded-none h-9 text-sm"
                  data-testid="product-search"
                />
                {searchLoading && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" size={14} />}
              </div>
            )}
          </div>
          {/* Minimal Step Breadcrumb */}
          <div className="flex items-center gap-1 text-sm" data-testid="step-indicator">
            {[
              { id: 1, label: "Products" },
              { id: 2, label: "Designs" },
              { id: 3, label: "Summary" },
            ].map((s, i) => (
              <span key={s.id} className="flex items-center gap-1">
                {i > 0 && <ChevronRight size={14} className="text-slate-300" />}
                <span className={`flex items-center gap-1.5 px-2 py-1 ${
                  step === s.id ? "bg-slate-900 text-white font-semibold" :
                  step > s.id ? "text-green-700 font-medium" : "text-slate-400"
                }`}>
                  {step > s.id && <Check size={13} className="text-green-600" />}
                  <span className="text-xs uppercase tracking-wider">{s.label}</span>
                </span>
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto pb-4">
        {/* ==================== STEP 1: SELECT PRODUCTS ==================== */}
        {step === 1 && (
          <div className="space-y-3" data-testid="step-1-content">
            {/* Groups — desktop: wrap + vertical scroll; mobile: horizontal scroll strip */}
            <div className="md:max-h-20 md:overflow-y-auto overflow-x-auto md:border md:border-slate-200 md:p-2" data-testid="group-tabs">
              <div className="flex md:flex-wrap gap-1 w-max md:w-auto">
                <button
                  onClick={() => { setSelectedGroup(null); setCurrentPage(1); }}
                  className={`text-[11px] px-2.5 py-1 font-bold whitespace-nowrap transition-colors ${selectedGroup === null ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
                  data-testid="group-all"
                >
                  All
                </button>
                {groups.map(group => (
                  <button
                    key={group}
                    onClick={() => { setSelectedGroup(group); setCurrentPage(1); }}
                    className={`text-[11px] px-2.5 py-1 font-semibold whitespace-nowrap transition-colors ${selectedGroup === group ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
                    data-testid={`group-${group}`}
                  >
                    {group}
                  </button>
                ))}
              </div>
            </div>

            {/* Products Count */}
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>Showing {products.length} of {totalProducts.toLocaleString()} products</span>
              <span>Page {currentPage} of {totalPages}</span>
            </div>

            {/* Mobile: product cards */}
            {searchLoading && products.length === 0 ? (
              <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : products.length === 0 ? (
              <div className="flex flex-col items-center py-10 text-slate-400">
                <Package className="w-10 h-10 mb-2 opacity-20" />
                <p className="text-sm">No products found</p>
              </div>
            ) : (
              <>
                {/* Mobile cards */}
                <div className="md:hidden space-y-2">
                  {sortedProducts.map(product => {
                    const isSelected = selectedProducts[0]?.id === product.id;
                    const rate = product.display_rate ? `₹${product.display_rate}` : `₹${product.base_rate_per_sqmm}/sqmm`;
                    const subtitle = [product.list_1, product.list_2].filter(Boolean).join(" · ");
                    return (
                      <div
                        key={product.id}
                        onClick={() => selectProduct(product)}
                        className={`border p-3.5 cursor-pointer transition-all active:scale-[0.99] ${
                          isSelected
                            ? "border-orange-500 bg-orange-50 border-l-4"
                            : "border-slate-200 bg-white"
                        }`}
                        data-testid={`product-row-${product.id}`}
                      >
                        <p className="font-semibold text-sm text-slate-900 leading-tight">{product.product_name}</p>
                        {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
                        <div className="flex items-center gap-3 mt-2 text-xs font-mono text-slate-600">
                          {product.width && <span>W {product.width} mm</span>}
                          {product.thickness && <span className="text-slate-400">·</span>}
                          {product.thickness && <span>T {product.thickness} mm</span>}
                          <span className="ml-auto font-semibold text-slate-800">{rate}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Desktop table */}
                <Card className="card-industrial overflow-hidden hidden md:block">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="bg-slate-900 text-white cursor-pointer select-none hover:bg-slate-700" onClick={() => handleStep1Sort("product_name")}>
                          <span className="inline-flex items-center">Product Name<Step1SortIcon field="product_name" /></span>
                        </TableHead>
                        <TableHead className="bg-slate-900 text-white">List 1</TableHead>
                        <TableHead className="bg-slate-900 text-white">List 2</TableHead>
                        <TableHead className="bg-slate-900 text-white text-right cursor-pointer select-none hover:bg-slate-700" onClick={() => handleStep1Sort("width")}>
                          <span className="inline-flex items-center justify-end w-full">Width<Step1SortIcon field="width" /></span>
                        </TableHead>
                        <TableHead className="bg-slate-900 text-white text-right cursor-pointer select-none hover:bg-slate-700" onClick={() => handleStep1Sort("thickness")}>
                          <span className="inline-flex items-center justify-end w-full">Thickness<Step1SortIcon field="thickness" /></span>
                        </TableHead>
                        <TableHead className="bg-slate-900 text-white text-right cursor-pointer select-none hover:bg-slate-700" onClick={() => handleStep1Sort("display_rate")}>
                          <span className="inline-flex items-center justify-end w-full">Rate<Step1SortIcon field="display_rate" /></span>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedProducts.map(product => {
                        const isSelected = selectedProducts[0]?.id === product.id;
                        return (
                          <TableRow
                            key={product.id}
                            className={`cursor-pointer transition-colors ${isSelected ? 'bg-orange-50 border-l-2 border-l-orange-500' : 'hover:bg-slate-50'}`}
                            onClick={() => selectProduct(product)}
                            data-testid={`product-row-${product.id}`}
                          >
                            <TableCell className="font-medium text-sm">{product.product_name}</TableCell>
                            <TableCell className="text-sm text-slate-600">{product.list_1 || "—"}</TableCell>
                            <TableCell className="text-sm text-slate-600">{product.list_2 || "—"}</TableCell>
                            <TableCell className="font-mono text-sm text-right">{product.width}</TableCell>
                            <TableCell className="font-mono text-sm text-right">{product.thickness || '-'}</TableCell>
                            <TableCell className="font-mono text-sm text-right">
                              {product.display_rate ? `₹${product.display_rate}` : `₹${product.base_rate_per_sqmm}/sqmm`}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Card>
              </>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 pt-1" data-testid="product-pagination">
                <Button variant="outline" size="sm" className="rounded-none h-8" disabled={currentPage <= 1} onClick={() => setCurrentPage(p => p - 1)} data-testid="prev-page-btn">
                  <ChevronLeft size={15} />
                </Button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) pageNum = i + 1;
                  else if (currentPage <= 3) pageNum = i + 1;
                  else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                  else pageNum = currentPage - 2 + i;
                  return (
                    <Button key={pageNum} variant={currentPage === pageNum ? "default" : "outline"} size="sm" className="rounded-none w-8 h-8" onClick={() => setCurrentPage(pageNum)} data-testid={`page-btn-${pageNum}`}>
                      {pageNum}
                    </Button>
                  );
                })}
                <Button variant="outline" size="sm" className="rounded-none h-8" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)} data-testid="next-page-btn">
                  <ChevronRight size={15} />
                </Button>
              </div>
            )}
          </div>
        )}

        {/* ==================== STEP 2: UPLOAD DESIGNS ==================== */}
        {step === 2 && (
          <div className="space-y-3" data-testid="step-2-content">
            <p className="text-sm text-slate-600 mb-2">Upload a design file and set the quantity</p>
            {orderItems.map((item, index) => (
              <Card key={item.product.id} className="card-industrial">
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="font-mono text-xs text-slate-500">{item.product.product_code}</p>
                      <p className="font-medium text-sm truncate">{item.product.product_name}</p>
                    </div>
                    <div className="flex-1">
                      {uploading[item.product.id] ? (
                        <div className="flex items-center gap-2 text-slate-500 text-sm">
                          <Loader2 size={16} className="animate-spin" /> Uploading...
                        </div>
                      ) : item.design ? (
                        <div className="flex items-center gap-2 text-green-600">
                          <FileCheck size={16} />
                          <span className="text-sm truncate">{item.design.filename}</span>
                          <span className="text-xs text-slate-500">({(item.design.calculated_area_sqmm / 1000000).toFixed(2)} m²)</span>
                        </div>
                      ) : (
                        <label className="cursor-pointer">
                          <Input type="file" accept=".pds,.dxf,.pdf,.ai,.cdr,.plt" className="hidden" onChange={(e) => e.target.files?.[0] && handleFileUpload(item.product.id, e.target.files[0], index)} data-testid={`file-input-${index}`} />
                          <span className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 hover:bg-slate-50 text-sm">
                            <Upload size={15} /> Upload Design
                          </span>
                        </label>
                      )}
                    </div>
                    <div className="w-44">
                      <Label className="text-xs uppercase tracking-wider">Component Name</Label>
                      <Input type="text" placeholder="e.g. Upper Sole" value={item.component_name} onChange={(e) => updateComponentName(index, e.target.value)} className="rounded-none mt-1 text-sm" data-testid={`component-name-${index}`} />
                    </div>
                    <div className="w-24">
                      <Label className="text-xs uppercase tracking-wider">Qty</Label>
                      <Input type="number" min="1" value={item.quantity} onChange={(e) => updateQuantity(index, parseInt(e.target.value) || 1)} onFocus={(e) => e.target.select()} className="rounded-none font-mono mt-1" data-testid={`quantity-input-${index}`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* ==================== STEP 3: ORDER SUMMARY ==================== */}
        {step === 3 && (
          <div className="space-y-5" data-testid="step-3-content">
            {loadingCost ? (
              <div className="flex flex-col items-center justify-center h-64 gap-4">
                <div className="relative">
                  <div className="w-16 h-16 border-4 border-slate-200 border-t-slate-900 animate-spin rounded-full"></div>
                  <Calculator size={20} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-slate-600" />
                </div>
                <div className="text-center">
                  <p className="text-slate-700 font-medium">Calculating costs...</p>
                  <p className="text-xs text-slate-400 mt-1">Running nesting algorithm & cost estimation</p>
                </div>
              </div>
            ) : (
              <>

                {/* Items */}
                {(costPreview?.items || []).map((item, index) => (
                  <div key={index} className="space-y-3" data-testid={`order-item-summary-${index}`}>

                    {/* ARTICLE DETAILS — Compact single-row layout (Changes #8, #9, #10) */}
                    <div className="border border-slate-200 bg-white">
                      <div className="bg-slate-900 text-white px-4 py-2">
                        <h3 className="text-xs font-semibold uppercase tracking-wider">Article Details</h3>
                      </div>
                      <div className="px-4 py-3">
                        {/* Row 1: Article Name + Type badge */}
                        <div className="flex items-center gap-3 mb-3">
                          <h4 className="text-base font-bold text-slate-900" data-testid="article-name">{item.product_name}</h4>
                          <Badge variant="outline" className="rounded-none text-[10px] uppercase" data-testid="article-type">{item.material_type}</Badge>
                        </div>
                        {/* Row 2: Compact metrics in a horizontal strip */}
                        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
                          <div className="flex items-center gap-1.5">
                            <span className="text-slate-500 text-xs">Width:</span>
                            <span className="font-mono font-medium" data-testid="material-width">{item.product_width_mm?.toLocaleString()} mm</span>
                          </div>
                          <div className="h-4 w-px bg-slate-200" />
                          <div className="flex items-center gap-1.5" data-testid="price-combined">
                            <span className="text-slate-500 text-xs">Price:</span>
                            <span className="font-mono font-semibold">₹{item.applied_rate}/{item.applied_rate_uom === "SQM" ? "sq.m" : item.applied_rate_uom === "MTR" ? "mtr" : item.applied_rate_uom?.toLowerCase() || "unit"}</span>
                            {item.applied_rate_uom !== "SQM" && (
                              <span className="text-slate-500 font-mono text-xs">(₹{getPricePerSqMeter(item).toFixed(2)}/sq.m)</span>
                            )}
                          </div>
                          <div className="h-4 w-px bg-slate-200" />
                          <div className="flex items-center gap-1.5">
                            <span className="text-slate-500 text-xs">Qty:</span>
                            <span className="font-mono font-medium" data-testid="quantity">{item.quantity} pcs</span>
                          </div>
                          <div className="h-4 w-px bg-slate-200" />
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex items-center gap-1.5 cursor-help">
                                  <span className="text-slate-500 text-xs">Pcs/Sq.M:</span>
                                  <span className="font-mono font-medium" data-testid="pcs-per-sqm">{item.pcs_per_sqm ? Math.round(item.pcs_per_sqm) : "—"}</span>
                                  <Info size={13} className="text-slate-400" />
                                </div>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-xs text-xs">
                                <p className="font-semibold mb-1">Pieces per Square Meter</p>
                                <p>How many pieces of this design can be cut from 1 square meter of material. Higher value = more efficient material usage.</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <div className="h-4 w-px bg-slate-200" />
                          <div className="flex items-center gap-1.5">
                            <span className="text-slate-500 text-xs">Wastage:</span>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="font-mono font-medium text-red-600 cursor-help" data-testid="wastage-pct">
                                    {item.wastage_pct?.toFixed(1)}%
                                    {(item.nesting_sheets_used > 1) && <span className="text-[10px] text-slate-400 ml-1">/{item.nesting_sheets_used}sh</span>}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-xs text-xs">
                                  {(item.nesting_sheets_used > 1)
                                    ? <p>Combined wastage across all <strong>{item.nesting_sheets_used} sheets</strong>. Sheet 1 is fully utilised; later sheets carry the remaining pieces causing higher overall wastage.</p>
                                    : <p>Material wastage — unused area after nesting all pieces on the sheet.</p>
                                  }
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* COST BREAKDOWN TABLE */}
                    <div className="border border-slate-200 bg-white">
                      <div className="bg-slate-900 text-white px-4 py-2">
                        <h3 className="text-xs font-semibold uppercase tracking-wider">Cost Breakdown</h3>
                      </div>
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="bg-slate-100 text-slate-700 font-semibold text-[11px] uppercase">Component</TableHead>
                              <TableHead className="bg-slate-100 text-slate-700 font-semibold text-[11px] uppercase text-right">Comp. Qty</TableHead>
                              <TableHead className="bg-slate-100 text-slate-700 font-semibold text-[11px] uppercase text-right">Per Piece</TableHead>
                              <TableHead className="bg-slate-100 text-slate-700 font-semibold text-[11px] uppercase text-right">Net Total</TableHead>
                              <TableHead className="bg-slate-100 text-slate-700 font-semibold text-[11px] uppercase text-right">Rate</TableHead>
                              <TableHead className="bg-slate-100 text-slate-700 font-semibold text-[11px] uppercase text-right">Cost/Piece</TableHead>
                              <TableHead className="bg-slate-100 text-slate-700 font-semibold text-[11px] uppercase text-right">Total Cost</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {/* Material Row */}
                            {(() => {
                              const { perQty, net, rate } = getMaterialDisplay(item);
                              const matCostPerPiece = item.quantity > 0 ? item.material_cost / item.quantity : 0;
                              return (
                                <TableRow>
                                  <TableCell className="text-sm">
                                    <span className="font-medium">{item.component_name || item.filename}</span>
                                    <span className="text-[10px] text-slate-400 ml-1">({item.product_name})</span>
                                  </TableCell>
                                  <TableCell className="text-right font-mono text-sm">{item.quantity}</TableCell>
                                  <TableCell className="text-right font-mono text-sm">{perQty}</TableCell>
                                  <TableCell className="text-right font-mono text-sm">{net}</TableCell>
                                  <TableCell className="text-right font-mono text-sm">{rate}</TableCell>
                                  <TableCell className="text-right font-mono text-sm">₹{matCostPerPiece.toFixed(2)}</TableCell>
                                  <TableCell className="text-right font-mono text-sm font-medium">₹{item.material_cost?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</TableCell>
                                </TableRow>
                              );
                            })()}
                            {/* Cutting Row */}
                            {(() => {
                              const cutRatePerPc = item.cutting_rate_per_running_mm || 0;
                              const noCut = cutRatePerPc === 0;
                              return (
                                <TableRow className={noCut ? "opacity-50" : ""}>
                                  <TableCell className="text-sm">
                                    <span className="font-medium">{item.component_name || item.filename}</span>
                                    <span className="text-[10px] text-slate-400 ml-1">(Cutting)</span>
                                  </TableCell>
                                  <TableCell className="text-right font-mono text-sm">{item.quantity}</TableCell>
                                  <TableCell className="text-right font-mono text-sm">{noCut ? "—" : `₹${cutRatePerPc.toFixed(2)}/pc`}</TableCell>
                                  <TableCell className="text-right font-mono text-sm">{item.quantity} pcs</TableCell>
                                  <TableCell className="text-right font-mono text-sm">{noCut ? "—" : `₹${cutRatePerPc.toFixed(2)}/pc`}</TableCell>
                                  <TableCell className="text-right font-mono text-sm">{noCut ? "—" : `₹${cutRatePerPc.toFixed(2)}`}</TableCell>
                                  <TableCell className="text-right font-mono text-sm font-medium">₹{item.cutting_cost?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</TableCell>
                                </TableRow>
                              );
                            })()}
                            {/* Total Row */}
                            <TableRow className="bg-slate-900 text-white">
                              <TableCell className="font-bold uppercase tracking-wider text-white text-sm" colSpan={5}>Total Order Cost</TableCell>
                              <TableCell className="text-right font-mono font-bold text-white text-sm" data-testid="total-per-piece">₹{item.quantity > 0 ? (item.item_total / item.quantity).toFixed(2) : "0.00"}</TableCell>
                              <TableCell className="text-right font-mono font-bold text-orange-400 text-base" data-testid="item-grand-total">₹{item.item_total?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </div>
                    </div>

                    {/* NESTING DIAGRAM */}
                    <div className="border border-slate-200 bg-white">
                      <div className="bg-slate-900 text-white px-4 py-2">
                        <h3 className="text-xs font-semibold uppercase tracking-wider">Nesting Layout</h3>
                      </div>
                      <div className="grid grid-cols-1 lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-slate-200">
                        <div className="lg:col-span-2 p-4">
                          <NestingDiagram
                            placements={item.placements || []}
                            rollWidth={item.product_width_mm || 1000}
                            rollLength={item.nesting_length_used_mm || 1000}
                            materialType={item.material_type}
                            totalSheets={item.nesting_sheets_used || 1}
                          />
                          <p className="text-center text-[10px] text-slate-400 mt-2">
                            Width: {item.product_width_mm?.toLocaleString()} mm | Length: {item.nesting_length_used_mm?.toLocaleString()} mm
                            {(item.nesting_sheets_used > 1) && ` | ${item.nesting_sheets_used} sheets`}
                          </p>
                        </div>
                        <div className="p-4 space-y-2 bg-slate-50 text-sm">
                          <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-700 border-b pb-1.5">Dimensions</h4>
                          <div className="space-y-1.5">
                            {[
                              ["Material Width", `${item.product_width_mm?.toLocaleString()} mm`],
                              ["Sheet Length", `${item.nesting_length_used_mm?.toLocaleString()} mm`],
                              ["Sheets Used", `${item.nesting_sheets_used ?? 1}`],
                              ["Piece Area", `${((item.shapes_area_sqmm || 0) / 1_000_000).toFixed(4)} sq.m`],
                              ["Material Consumed", `${(item.material_consumed_sqmm / 1000000).toFixed(4)} sq.m`],
                              ["Pieces Count", item.pieces_count?.toLocaleString()],
                              ["Cutting Rate", item.cutting_rate_per_running_mm > 0 ? `₹${item.cutting_rate_per_running_mm.toFixed(2)}/pc` : "—"],
                            ].map(([label, val]) => {
                              const isSheets = label === "Sheets Used";
                              const sheetsVal = item.nesting_sheets_used ?? 1;
                              return (
                                <div key={label} className="flex justify-between items-center">
                                  <span className="text-slate-500 text-xs">{label}</span>
                                  <span className={`font-mono font-medium text-xs ${isSheets && sheetsVal > 1 ? "bg-orange-100 text-orange-700 px-1.5 py-0.5" : ""}`}>{val}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* OPTIMAL QTY SUGGESTION */}
                    {(() => {
                      const suggestion = suggestOptimalQty(item);
                      if (!suggestion) return null;
                      const isUp = suggestion.direction === "up";
                      return (
                        <div className={`border p-4 flex flex-col sm:flex-row sm:items-center gap-3 ${isUp ? "border-amber-300 bg-amber-50" : "border-blue-200 bg-blue-50"}`} data-testid={`qty-suggestion-${index}`}>
                          <div className="flex-1">
                            <p className={`text-sm font-semibold ${isUp ? "text-amber-800" : "text-blue-800"}`}>
                              {isUp ? "💡 Better Material Utilisation" : "💡 Save Material Cost"}
                            </p>
                            <p className={`text-sm mt-0.5 ${isUp ? "text-amber-700" : "text-blue-700"}`}>{suggestion.message}</p>
                            <p className={`text-xs mt-1 font-mono ${isUp ? "text-amber-600" : "text-blue-600"}`}>{suggestion.detail}</p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className={`text-sm font-mono font-bold ${isUp ? "text-amber-800" : "text-blue-800"}`}>
                              Qty: {suggestion.suggestedQty}
                            </span>
                            <button
                              onClick={async () => {
                                const updatedItems = orderItems.map((oi, i) =>
                                  i === index ? { ...oi, quantity: suggestion.suggestedQty } : oi
                                );
                                setOrderItems(updatedItems);
                                await recalculateCost(updatedItems);
                              }}
                              disabled={loadingCost}
                              className={`px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 ${isUp ? "bg-amber-600 hover:bg-amber-700" : "bg-blue-600 hover:bg-blue-700"}`}
                            >
                              {loadingCost ? "Recalculating…" : "Apply & Recalculate"}
                            </button>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                ))}

                {/* DESIRED DELIVERY SCHEDULE */}
                {costPreview?.items?.length > 0 && (
                  <div className="border border-slate-200 bg-white" data-testid="delivery-schedule-section">
                    <div className="bg-slate-900 text-white px-4 py-2 flex items-center justify-between">
                      <h3 className="text-xs font-semibold uppercase tracking-wider">Desired Delivery Schedule</h3>
                      <span className="text-[10px] text-slate-400">Optional — split quantity across multiple dates</span>
                    </div>
                    <div className="p-4 space-y-3">
                      {/* Table */}
                      <div className="border border-slate-200">
                        <div className="grid grid-cols-[1fr_160px_36px] gap-2 bg-slate-100 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-600">
                          <span>Delivery Date</span>
                          <span>Quantity (pcs)</span>
                          <span></span>
                        </div>
                        {deliverySchedule.length === 0 && (
                          <div className="px-3 py-4 text-center text-xs text-slate-400">
                            No schedule added — single delivery will be assumed.
                          </div>
                        )}
                        {deliverySchedule.map((row, idx) => (
                          <div key={idx} className="grid grid-cols-[1fr_160px_36px] gap-2 px-3 py-2 border-t border-slate-100 items-center" data-testid={`schedule-row-${idx}`}>
                            <Input
                              type="date"
                              value={row.date}
                              onChange={(e) => updateScheduleRow(idx, "date", e.target.value)}
                              className="rounded-none h-8 text-sm"
                            />
                            <Input
                              ref={el => { scheduleQtyRefs.current[idx] = el; }}
                              type="number"
                              min="1"
                              value={row.quantity}
                              onChange={(e) => updateScheduleRow(idx, "quantity", parseInt(e.target.value) || 1)}
                              onFocus={(e) => e.target.select()}
                              className="rounded-none font-mono h-8 text-sm"
                            />
                            <button
                              type="button"
                              onClick={() => removeScheduleRow(idx)}
                              className="flex items-center justify-center w-7 h-7 text-slate-400 hover:text-red-600 hover:bg-red-50 border border-slate-200"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ))}
                      </div>

                      {/* Qty validation */}
                      {deliverySchedule.length > 0 && (() => {
                        const total = deliverySchedule.reduce((s, r) => s + (parseInt(r.quantity) || 0), 0);
                        const ordered = orderItems[0]?.quantity || 0;
                        const match = total === ordered;
                        return (
                          <div className={`text-xs px-2.5 py-1.5 flex items-center gap-2 ${match ? "bg-green-50 text-green-700 border border-green-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>
                            <span>{match ? "✓" : "⚠"}</span>
                            <span>Scheduled: <strong>{total}</strong> pcs &nbsp;/&nbsp; Ordered: <strong>{ordered}</strong> pcs{!match && " — totals don't match"}</span>
                          </div>
                        );
                      })()}

                      <Button
                        type="button"
                        onClick={addScheduleRow}
                        variant="outline"
                        className="h-8 rounded-none border-slate-300 text-slate-700 hover:bg-slate-50 text-xs"
                        data-testid="add-schedule-row-btn"
                      >
                        <Plus size={14} className="mr-1.5" />
                        Add Delivery Date
                      </Button>
                    </div>
                  </div>
                )}

                {/* GRAND TOTAL + ACTIONS */}
                {costPreview?.items?.length > 0 && (
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div className="lg:col-span-2 border-2 border-slate-800 bg-slate-900 text-white p-5">
                      <h3 className="text-base font-bold uppercase tracking-wider mb-3">Order Summary</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between items-center text-slate-300">
                          <span>{costPreview.items.length} component(s) — material + cutting</span>
                          <span className="font-mono">₹{costPreview.grand_total?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                        </div>
                        <div className="border-t border-slate-700 pt-2 flex justify-between items-center">
                          <span className="font-bold uppercase tracking-wider">Grand Total</span>
                          <span className="text-3xl font-mono font-bold text-orange-400" data-testid="grand-total">
                            ₹{costPreview.grand_total?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                          </span>
                        </div>
                      </div>
                      <p className="text-[10px] text-slate-500 mt-3">* GST will be Charged Extra as Applicable</p>
                      <p className="text-[10px] text-slate-500">* Freight cost will be added by sales team before confirmation</p>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs uppercase tracking-wider">Notes</Label>
                        <Textarea value={customerNotes} onChange={(e) => setCustomerNotes(e.target.value)} placeholder="Special instructions..." className="rounded-none mt-1" rows={2} data-testid="customer-notes-input" />
                      </div>
                      <Button onClick={handleSaveDraft} disabled={submitting} variant="outline" className="w-full h-9 rounded-none text-sm" data-testid="save-draft-button">
                        Save Draft
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Fixed Bottom Navigation Bar */}
      <div className="fixed bottom-0 right-0 bg-white border-t border-slate-200 py-3 px-6 z-30 left-0 lg:left-64">
        <div className="flex justify-between items-center">
          {step > 1 ? (
            <Button variant="outline" onClick={() => setStep(step - 1)} className="rounded-none h-11 px-5" data-testid="prev-step-button">
              <ArrowLeft size={16} className="mr-2" /> Previous
            </Button>
          ) : <div />}
          <div className="text-xs text-slate-400">Step {step} of 3</div>
          {step < 3 ? (
            <Button
              onClick={() => goToStep(step + 1)}
              disabled={(step === 1 ? !canProceedToStep2 : !canProceedToStep3) || loadingCost}
              className="bg-slate-900 text-white rounded-none h-11 px-5 btn-industrial"
              data-testid="next-step-button"
            >
              {loadingCost ? (
                <span className="flex items-center gap-2">
                  <Loader2 size={16} className="animate-spin" /> Calculating...
                </span>
              ) : (
                <>Next <ArrowRight size={16} className="ml-2" /></>
              )}
            </Button>
          ) : (
            <Button
              onClick={handlePlaceOrder}
              disabled={submitting}
              className="bg-orange-500 hover:bg-orange-600 text-white rounded-none h-11 px-6 btn-industrial"
              data-testid="place-order-button-main"
            >
              {submitting ? (
                <span className="flex items-center gap-2">
                  <Loader2 size={16} className="animate-spin" /> Processing Order...
                </span>
              ) : "Place Order"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
